/**
 * Signup flow state (V1 auth-profiles-gdpr) — carries the S-02 DOB into the
 * S-04 account step and the signup email into the S-05 pending screen.
 *
 * dob + email survive a refresh via sessionStorage (same-tab only; cleared on
 * completion); the password NEVER leaves memory — it exists only so the flow
 * can auto-login right after the 202 signup (e2e cohort / pre-verified paths).
 */
import { createStore } from "../../lib/store.js";

const DOB_KEY = "humble.signup_dob";
const EMAIL_KEY = "humble.signup_email";
const LOCK_KEY = "humble.underage_lock_ref";

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* private mode — memory store still works */
  }
}

export interface SignupFlowState {
  dob: string | null;
  email: string | null;
  /** memory-only, never persisted (auto-login right after signup). */
  password: string | null;
}

export const signupFlow = createStore<SignupFlowState>({
  dob: readSession(DOB_KEY),
  email: readSession(EMAIL_KEY),
  password: null,
});

export function setSignupDob(dob: string): void {
  signupFlow.set({ dob });
  writeSession(DOB_KEY, dob);
}

export function setSignupAccount(email: string, password: string | null): void {
  signupFlow.set({ email, password });
  writeSession(EMAIL_KEY, email);
}

export function clearSignupFlow(): void {
  signupFlow.set({ dob: null, email: null, password: null });
  writeSession(DOB_KEY, null);
  writeSession(EMAIL_KEY, null);
}

// ---------------------------------------------------------------------------
// Underage device-lock marker (S-02/S-03). The REAL lock is server-side
// (underage_locks row keyed on the device fingerprint — SM-12); this marker
// only lets the client route straight to S-03 on load/refresh without a
// round-trip. Clearing it changes nothing server-side.
// ---------------------------------------------------------------------------
export function readUnderageLockRef(): string | null {
  try {
    return localStorage.getItem(LOCK_KEY);
  } catch {
    return null;
  }
}

export function writeUnderageLockRef(lockRef: string): void {
  try {
    localStorage.setItem(LOCK_KEY, lockRef);
  } catch {
    /* non-fatal */
  }
}

/** SM-12 lifecycle → the screen that owns it (signup/onboarding routing). */
export function routeForLifecycle(lifecycle: string | null | undefined): string {
  switch (lifecycle) {
    case "registered_unverified":
      return "/verify-email";
    case "onboarding":
      return "/onboard/identity";
    case "deletion_grace":
      return "/grace";
    case "banned":
      return "/banned";
    default:
      return "/deck";
  }
}

/** Browser IANA timezone with a safe fallback (signup body `tz`). */
export function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
